import { useEffect, useMemo, useState } from 'react';
import { contactsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Users, Plus, RefreshCw, Merge, Trash2, Loader2, Download } from 'lucide-react';
import { toast } from 'sonner';
import ConfirmActionDialog from '@/components/ConfirmActionDialog';
import OrgProfileAttributes, { orgProfileSummary } from '@/components/OrgProfileAttributes';
import OrganigramaDeLaPersona from '@/components/OrganigramaDeLaPersona';
import ContactIngestDialog, { IngestSourceBadges } from '@/components/ContactIngestDialog';
import MemoriasDeLaPersona from '@/components/MemoriasDeLaPersona';
import { useAuth } from '@/hooks/useAuth';

// Un numero de telefono es una identidad `phone`, no `whatsapp`: el mismo aparato sirve para
// que la persona escriba y para que el sistema le notifique. Partirlo por canal era lo que
// hacia que la misma persona existiera dos veces.
const newIdentity = (channel = 'phone') => ({
  identity_id: `tmp-${Date.now()}-${Math.random()}`,
  channel_type: channel,
  raw_value: '',
  is_primary: false,
  // Marcado como móvil por defecto: nadie escribe su celular con el 9 que exige el formato
  // internacional. Se tipea "351 683-6875", que leído como fijo nunca matchea con el número
  // que llega por WhatsApp — y ahí nace una segunda persona para el mismo humano.
  es_movil: true,
});

const displayIdentityValue = (identity) => {
  const channel = (identity?.channel_type || '').toLowerCase();
  const raw = identity?.raw_value || '';
  const normalized = identity?.external_id_normalized || '';
  if (channel === 'phone' || channel === 'whatsapp') return normalized || raw;
  return raw;
};

const EMPTY_FORM = {
  display_name: '',
  tags: '',
  notes: '',
  identities: [{ ...newIdentity('phone'), is_primary: true }],
};

export default function PersonasPage() {
  const { user } = useAuth();
  // Sólo admin. Una memoria dice cómo conviene atender a alguien y esa persona no la ve:
  // el backend exige el rol y acá no se muestra siquiera el desplegable, para que nadie
  // se entere de que existe algo que no va a poder abrir.
  const esAdmin = user?.role === 'admin';
  const tenantId = user?.tenant_id || '';
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [mergeSource, setMergeSource] = useState('');
  const [mergeTarget, setMergeTarget] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ display_name: '', tags: '', notes: '', identities: [] });
  const [confirmState, setConfirmState] = useState({ open: false, title: '', description: '', onConfirm: null, confirmText: 'Confirmar' });
  const [syncingOrg, setSyncingOrg] = useState(false);
  const [syncingOneId, setSyncingOneId] = useState(null);
  const [ingestOpen, setIngestOpen] = useState(false);

  const loadContacts = async () => {
    setLoading(true);
    try {
      const { data } = await contactsAPI.list(query ? { q: query } : undefined);
      setContacts(data || []);
    } catch {
      toast.error('No se pudieron cargar las personas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContacts();
  }, []);

  // **El orden de la lista es el orden en que se intenta avisar.** Mover una fila es la única
  // forma de expresarlo sin inventar números ni resolver a mano qué pasa con dos iguales.
  const moverIdentidad = (setter, idx, delta) => {
    setter((prev) => {
      const filas = [...(prev.identities || [])];
      const destino = idx + delta;
      if (destino < 0 || destino >= filas.length) return prev;
      [filas[idx], filas[destino]] = [filas[destino], filas[idx]];
      return { ...prev, identities: filas };
    });
  };

  const normalizeIdentities = (identities) =>
    (identities || [])
      .map((i) => ({
        identity_id: i.identity_id,
        channel_type: i.channel_type,
        raw_value: (i.raw_value || '').trim(),
        is_primary: !!i.is_primary,
        avisar_por_aca: i.avisar_por_aca !== false,
        // Si no vino, es móvil: es el caso abrumadoramente más frecuente y el que hay que
        // acertar, porque es el que tiene que matchear con WhatsApp.
        es_movil: i.es_movil !== false,
      }))
      .filter((i) => i.raw_value);

  const createContact = async () => {
    const preparedIdentities = normalizeIdentities(form.identities).map(({ channel_type, raw_value, is_primary, es_movil, avisar_por_aca }, orden) => ({
      channel_type,
      raw_value,
      is_primary,
      es_movil,
    }));
    if (!form.display_name.trim() || preparedIdentities.length === 0) {
      toast.error('Completa nombre y al menos un canal');
      return;
    }
    setSaving(true);
    try {
      const { data } = await contactsAPI.create({
        display_name: form.display_name.trim(),
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
        notes: form.notes.trim() || null,
        identities: preparedIdentities,
      });
      setForm(EMPTY_FORM);
      if (data?.already_exists) {
        toast.warning('Esa identidad ya estaba cargada: se reutilizo la persona que ya existia');
      } else {
        toast.success('Persona creada');
      }
      await loadContacts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo crear la persona');
    } finally {
      setSaving(false);
    }
  };

  const handleSyncOrgAll = async () => {
    setSyncingOrg(true);
    try {
      const res = await contactsAPI.syncOrgProfiles();
      toast.success(
        `Organigrama: ${res.data?.synced_ok || 0} ok, ${res.data?.synced_error || 0} error, ${res.data?.skipped || 0} omitidos`
      );
      await loadContacts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error sync organigrama (¿skill Graph / User.Read.All?)');
    } finally {
      setSyncingOrg(false);
    }
  };

  const handleSyncOrgOne = async (contactId) => {
    setSyncingOneId(contactId);
    try {
      await contactsAPI.syncOrgProfileOne(contactId);
      toast.success('Organigrama actualizado');
      await loadContacts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error sync organigrama');
    } finally {
      setSyncingOneId(null);
    }
  };

  const mergeContacts = async () => {
    if (!mergeSource || !mergeTarget || mergeSource === mergeTarget) {
      toast.error('Selecciona origen y destino válidos');
      return;
    }
    try {
      await contactsAPI.merge(mergeTarget, mergeSource);
      setMergeSource('');
      setMergeTarget('');
      toast.success('Personas vinculadas');
      await loadContacts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo unificar');
    }
  };

  const deleteContact = async (contactId, displayName) => {
    if (!contactId) return;
    setConfirmState({
      open: true,
      title: 'Eliminar persona',
      description: `Se eliminara a "${displayName}". Los casos que haya pedido quedan sin solicitante identificable.`,
      confirmText: 'Eliminar',
      onConfirm: async () => {
        try {
          await contactsAPI.delete(contactId);
          toast.success('Persona eliminada');
          await loadContacts();
        } catch (err) {
          toast.error(err.response?.data?.detail || 'No se pudo eliminar la persona');
        }
      },
    });
  };

  const startEdit = (contact) => {
    setEditingId(contact.contact_id);
    setEditForm({
      display_name: contact.display_name || '',
      tags: (contact.tags || []).join(', '),
      notes: contact.notes || '',
      // Ordenadas como se van a intentar: si la pantalla las mostrara en el orden en que Mongo
      // las devuelve, mover una fila y volver a abrir mostraría otra cosa.
      identities: [...(contact.identities || [])]
        .sort((a, b) => (a.orden_para_avisar ?? 500) - (b.orden_para_avisar ?? 500))
        .map((i) => ({
        identity_id: i.identity_id,
        channel_type: i.channel_type,
        raw_value: displayIdentityValue(i),
        is_primary: !!i.is_primary,
        avisar_por_aca: i.avisar_por_aca !== false,
      })),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ display_name: '', tags: '', notes: '', identities: [] });
  };

  const saveEdit = async (contactId) => {
    const preparedIdentities = normalizeIdentities(editForm.identities);
    if (!editForm.display_name.trim() || preparedIdentities.length === 0) {
      toast.error('Nombre y al menos un canal son obligatorios');
      return;
    }
    try {
      await Promise.all([
        contactsAPI.update(contactId, {
          display_name: editForm.display_name.trim(),
          tags: editForm.tags.split(',').map((t) => t.trim()).filter(Boolean),
          notes: editForm.notes.trim() || null,
        }),
        contactsAPI.replaceIdentities(contactId, preparedIdentities),
      ]);
      toast.success('Persona actualizada');
      cancelEdit();
      await loadContacts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo actualizar la persona');
    }
  };

  const updateIdentity = (setter, idx, field, value) => {
    setter((prev) => {
      const identities = [...(prev.identities || [])];
      identities[idx] = { ...identities[idx], [field]: value };
      if (field === 'is_primary' && value) {
        for (let i = 0; i < identities.length; i += 1) {
          if (i !== idx) identities[i].is_primary = false;
        }
      }
      return { ...prev, identities };
    });
  };

  const addIdentityRow = (setter) => {
    setter((prev) => ({ ...prev, identities: [...(prev.identities || []), newIdentity('email')] }));
  };

  const removeIdentityRow = (setter, idx) => {
    setter((prev) => {
      const identities = [...(prev.identities || [])].filter((_, i) => i !== idx);
      if (!identities.length) identities.push({ ...newIdentity('phone'), is_primary: true });
      if (!identities.some((i) => i.is_primary)) identities[0].is_primary = true;
      return { ...prev, identities };
    });
  };

  const renderIdentityEditor = (state, setter) => (
    <div className="space-y-2">
      {(state.identities || []).map((i, idx) => (
        <div key={i.identity_id || idx} className="border border-brand-border rounded-sm p-2 space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
            <select
              className="md:col-span-3 w-full border border-brand-border rounded-sm h-9 px-2 text-xs"
              value={i.channel_type}
              onChange={(e) => updateIdentity(setter, idx, 'channel_type', e.target.value)}
            >
              <option value="phone">telefono / WhatsApp</option>
              <option value="email">email</option>
              <option value="teams">teams</option>
              <option value="telegram">telegram</option>
              <option value="webchat">webchat</option>
            </select>
            <Input
              className="md:col-span-7 h-9 text-xs"
              value={i.raw_value}
              placeholder="+54 9 351 683-6875, alguien@empresa.com, usuario..."
              onChange={(e) => updateIdentity(setter, idx, 'raw_value', e.target.value)}
            />
            {/* Las flechas son el orden: **la lista es la prioridad**. Un campo numérico
                obligaría a inventar números y a resolver a mano qué pasa con dos iguales. */}
            <div className="md:col-span-2 flex items-center justify-end gap-1">
              <Button type="button" variant="ghost" size="sm" className="h-8 px-1.5"
                      disabled={idx === 0} title="Probar antes por acá"
                      onClick={() => moverIdentidad(setter, idx, -1)}>↑</Button>
              <Button type="button" variant="ghost" size="sm" className="h-8 px-1.5"
                      title="Probar después por acá"
                      onClick={() => moverIdentidad(setter, idx, 1)}>↓</Button>
              <Button type="button" variant="ghost" size="sm" className="h-8 px-2 text-red-600"
                      onClick={() => removeIdentityRow(setter, idx)}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs pl-0.5">
            {(i.channel_type === 'phone' || i.channel_type === 'whatsapp') && (
              <label className="flex items-center gap-1"
                     title="Un celular escrito sin el 9 se lee como un fijo y no matchea con WhatsApp">
                <input type="checkbox" checked={i.es_movil !== false}
                       onChange={(e) => updateIdentity(setter, idx, 'es_movil', e.target.checked)} />
                <span className="text-zinc-600">móvil</span>
              </label>
            )}
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={!!i.is_primary}
                     onChange={(e) => updateIdentity(setter, idx, 'is_primary', e.target.checked)} />
              <span className="text-zinc-600">Principal</span>
            </label>
            {/* **«Avisar por acá» y no «borrar el canal».** Si alguien escribe desde su WhatsApp
                personal, ese número sigue siendo suyo para reconocerlo aunque no queramos
                escribirle ahí. Borrar la identidad por la que entró un caso rompe su
                trazabilidad; apagarla para avisos no rompe nada. */}
            <label className="flex items-center gap-1" data-testid={`avisar-${idx}`}>
              <input type="checkbox" checked={i.avisar_por_aca !== false}
                     onChange={(e) => updateIdentity(setter, idx, 'avisar_por_aca', e.target.checked)} />
              <span className="text-zinc-600">Avisar por acá</span>
            </label>
            {idx === 0 && i.avisar_por_aca !== false && (
              <span className="text-[10px] text-zinc-400">← se intenta primero</span>
            )}
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" className="h-8 rounded-sm" onClick={() => addIdentityRow(setter)}>
        <Plus className="w-3 h-3 mr-1" />
        Agregar canal
      </Button>
    </div>
  );

  const contactOptions = useMemo(
    () => contacts.map((c) => ({ id: c.contact_id, label: `${c.display_name} (${c.contact_id.slice(0, 8)})` })),
    [contacts]
  );

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="contacts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#09090B]">Personas</h1>
          <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">
            Identidades omnicanal por tenant (WhatsApp, Email, Teams, Telegram, etc.)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button className="rounded-sm" onClick={() => setIngestOpen(true)} disabled={loading}>
            <Download className="w-4 h-4 mr-2" />
            Importar personas
          </Button>
          <Button
            variant="outline"
            className="rounded-sm"
            onClick={handleSyncOrgAll}
            disabled={syncingOrg || loading}
          >
            {syncingOrg ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Sync organigrama
          </Button>
          <Button variant="outline" className="rounded-sm" onClick={loadContacts} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>

      <Card className="rounded-sm border-brand-border">
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2"><Plus className="w-4 h-4" /> Nueva persona</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Nombre</Label>
              <Input value={form.display_name} onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs">Tags (coma)</Label>
              <Input value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs">Notas</Label>
              <Input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <div>
            <Label className="text-xs mb-1 block">Canales del contacto</Label>
            {renderIdentityEditor(form, setForm)}
          </div>
          <div className="flex justify-end">
            <Button className="rounded-sm" onClick={createContact} disabled={saving}>
              Crear contacto
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-sm border-brand-border">
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2"><Merge className="w-4 h-4" /> Unificar Duplicados</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Origen (se fusiona)</Label>
            <select className="w-full border border-brand-border rounded-sm h-10 px-2 text-sm" value={mergeSource} onChange={(e) => setMergeSource(e.target.value)}>
              <option value="">Seleccionar</option>
              {contactOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Destino (se conserva)</Label>
            <select className="w-full border border-brand-border rounded-sm h-10 px-2 text-sm" value={mergeTarget} onChange={(e) => setMergeTarget(e.target.value)}>
              <option value="">Seleccionar</option>
              {contactOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <Button variant="outline" className="w-full rounded-sm" onClick={mergeContacts}>Unificar</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-sm border-brand-border">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2"><Users className="w-4 h-4" /> Contactos</CardTitle>
          <div className="w-72">
            <Input placeholder="Buscar por nombre o identidad..." value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && loadContacts()} />
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {(contacts || []).map((c) => (
            <div key={c.contact_id} className="border border-brand-border rounded-sm p-3">
              <div className="flex items-center justify-between">
                <div className="w-full pr-3">
                  {editingId === c.contact_id ? (
                    <div className="space-y-2">
                      <Input value={editForm.display_name} onChange={(e) => setEditForm((f) => ({ ...f, display_name: e.target.value }))} placeholder="Nombre" className="h-8 text-sm" />
                      <Input value={editForm.tags} onChange={(e) => setEditForm((f) => ({ ...f, tags: e.target.value }))} placeholder="tags separadas por coma" className="h-8 text-xs" />
                      <Input value={editForm.notes} onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Notas" className="h-8 text-xs" />
                      <Label className="text-xs">Canales</Label>
                      {renderIdentityEditor(editForm, setEditForm)}
                    </div>
                  ) : (
                    <>
                      <p className="text-sm font-medium">{c.display_name}</p>
                      <p className="text-[11px] text-zinc-500 font-mono">{c.contact_id}</p>
                      {!!(c.tags || []).length && <p className="text-[11px] text-zinc-500">Tags: {(c.tags || []).join(', ')}</p>}
                      {!!c.notes && <p className="text-[11px] text-zinc-500">Notas: {c.notes}</p>}
                      {orgProfileSummary(c.org_profile) && (
                        <p className="text-[10px] text-zinc-600 mt-1">{orgProfileSummary(c.org_profile)}</p>
                      )}
                    </>
                  )}
                </div>
                <Badge variant="secondary">{c.status || 'active'}</Badge>
              </div>
              {editingId !== c.contact_id && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {(c.identities || []).map((i) => (
                    <Badge key={i.identity_id} variant="outline" className="text-[10px]">
                      {i.channel_type}: {displayIdentityValue(i)}{i.is_primary ? ' (principal)' : ''}
                    </Badge>
                  ))}
                </div>
              )}
              <IngestSourceBadges ingestion={c.ingestion} />
              {esAdmin && editingId !== c.contact_id && (
                <MemoriasDeLaPersona tenantId={tenantId} contactId={c.contact_id} nombre={c.display_name} />
              )}
              <div className="mt-3 space-y-2">
                {/* El organigrama **efectivo**, con la corrección humana encima y diciendo cuál
                    manda. Debajo queda el detalle crudo de M365, que sigue sirviendo para
                    diagnosticar un sync: son dos preguntas distintas —qué sabemos de esta
                    persona, y qué contestó el directorio la última vez—. */}
                <OrganigramaDeLaPersona
                  persona={c}
                  puedeEditar={esAdmin}
                  onCambio={loadContacts}
                  refreshing={syncingOneId === c.contact_id}
                  onRefresh={() => handleSyncOrgOne(c.contact_id)}
                />
                <OrgProfileAttributes
                  orgProfile={c.org_profile}
                  title="Lo que contestó Microsoft"
                />
              </div>
              <div className="mt-3 flex justify-end gap-2">
                {editingId === c.contact_id ? (
                  <>
                    <Button size="sm" variant="outline" className="h-8 px-3 rounded-sm" onClick={cancelEdit}>Cancelar</Button>
                    <Button size="sm" className="h-8 px-3 rounded-sm" onClick={() => saveEdit(c.contact_id)}>Guardar cambios</Button>
                  </>
                ) : (
                  <>
                    <Button size="sm" variant="outline" className="h-8 px-3 rounded-sm" onClick={() => startEdit(c)}>Editar</Button>
                    <Button size="sm" variant="destructive" className="h-8 px-3 rounded-sm" onClick={() => deleteContact(c.contact_id, c.display_name)}>
                      <Trash2 className="w-3 h-3 mr-1" />
                      Eliminar contacto
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
          {!contacts.length && <p className="text-xs text-zinc-500">No hay contactos cargados.</p>}
        </CardContent>
      </Card>
      <ConfirmActionDialog
        open={confirmState.open}
        onOpenChange={(open) => {
          if (!open) setConfirmState({ open: false, title: '', description: '', onConfirm: null, confirmText: 'Confirmar' });
        }}
        title={confirmState.title}
        description={confirmState.description}
        confirmText={confirmState.confirmText}
        onConfirm={confirmState.onConfirm}
      />
      <ContactIngestDialog
        open={ingestOpen}
        onOpenChange={setIngestOpen}
        onFinished={loadContacts}
      />
    </div>
  );
}
